import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Plus } from 'lucide-react'
import { useInvoiceCardFee } from '@/api/hooks'
import { AP_PAYMENT_POLICIES } from '@/features/invoices/constants'
import { InvoiceCardFeeLine } from '@/features/manufacturers/card-fee-field'
import {
  type ApInvoicePoLink,
  type ApPaymentPolicy,
  type ApInvoiceDetail,
} from '@/features/invoices/view-models'
import type { useInvoice, useInvoiceApprovalCapabilities } from '@/features/invoices/queries'
import type { useUpdateInvoice } from '@/features/invoices/mutations'
import { useUpdateInvoicePoLinkAllocation } from '@/features/invoices/data/actions'
import { ActionPopover } from '@/components/action-popover'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { POInlineEditableField } from '@/components/po-inline-edit'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ComposeTextarea } from '@/components/compose-field'
import { InlineFieldInput } from '@/components/inline-field-group'
import { formatMoney, formatStatus } from '@/lib/utils'
import { getInvoicePriorityStyle, getInvoiceStatusStyle } from '@/features/invoices/status'
import { compactDate, validateAmount, toNullable } from './formatting'
import {
  SummaryReadOnlyRow,
  InlineInvoiceField,
  InlineChoiceControl,
  InvoiceAssigneeControl,
} from './fields'
import { type useInvoiceSummary } from './use-summary'
import { getApiErrorMessage } from '@/lib/api-error'

function formatTerms(invoice: NonNullable<ReturnType<typeof useInvoice>['data']>) {
  const terms = invoice.payment_terms
  if (terms?.category && terms.category !== 'unknown') {
    // "Net" already says the category: "Net 30", not "Net · Net 30".
    const parts = terms.category === 'net' && terms.net_days != null ? [] : [formatStatus(terms.category)]
    if (terms.net_days != null) parts.push(`Net ${terms.net_days}`)
    if (terms.discount_percent != null && terms.discount_days != null) {
      parts.push(`${terms.discount_percent}% if paid in ${terms.discount_days} days`)
    }
    return parts.join(' · ')
  }
  return invoice.payment_terms_raw || 'Not set'
}

function PoAllocationRow({ invoiceId, link }: { invoiceId: string; link: ApInvoicePoLink }) {
  const updateAllocation = useUpdateInvoicePoLinkAllocation(invoiceId, link.id ?? 0)
  const poLabel = link.purchase_order?.po_number || link.po_number_raw || 'Unresolved PO'
  // Show what was typed while the save runs, instead of flashing the old allocation.
  const savingAmount = updateAllocation.isPending
    ? updateAllocation.variables?.allocation_amount
    : undefined
  const amount = savingAmount ?? link.allocation_amount
  const currentAllocation =
    amount != null
      ? `${formatMoney(amount)} allocated`
      : link.allocation_percent != null
        ? `${link.allocation_percent}% allocated`
        : 'Allocation needed'

  const save = (raw: string) => {
    if (!Number.isFinite(Number(raw)) || Number(raw) <= 0) return
    updateAllocation.mutate(
      { allocation_amount: raw, allocation_percent: null },
      {
        onSuccess: () => toast.success(`Allocation saved for ${poLabel}`),
        onError: (error) =>
          toast.error(getApiErrorMessage(error, 'Could not save this PO allocation.')),
      },
    )
  }

  return (
    <div className="min-w-0 px-3 py-2">
      {link.purchase_order?.uuid ? (
        <Link
          to="/purchase-orders/$poId"
          params={{ poId: link.purchase_order.uuid }}
          className="font-mono font-semibold text-primary hover:underline"
        >
          {poLabel}
        </Link>
      ) : (
        <span className="font-mono font-semibold text-muted-foreground">{poLabel}</span>
      )}
      <POInlineEditableField
        kind="money"
        value={amount ?? ''}
        displayValue={currentAllocation}
        ariaLabel={`Amount allocated to ${poLabel}`}
        title={`Amount allocated to ${poLabel}`}
        disabled={link.id == null}
        pending={updateAllocation.isPending}
        showEditIcon
        buttonClassName="-mx-2 flex min-h-6 py-0.5 text-xs text-muted-foreground"
        inputClassName="h-7 w-36"
        onConfirm={save}
      />
    </div>
  )
}

/** Status, priority and assignee, edited in place in the page header. */
export function InvoiceStateControls({
  invoice,
  summary,
  approvalCapabilities,
  updateInvoice,
}: {
  invoice: ApInvoiceDetail
  summary: ReturnType<typeof useInvoiceSummary>
  approvalCapabilities: ReturnType<typeof useInvoiceApprovalCapabilities>
  updateInvoice: ReturnType<typeof useUpdateInvoice>
}) {
  const { savePatch, statusOptions, priorityOptions } = summary
  return (
    <>
      <InlineChoiceControl
        value={invoice.status || 'unknown'}
        options={statusOptions}
        ariaLabel="Inline invoice status"
        disabled={
          updateInvoice.isPending ||
          approvalCapabilities.isLoading ||
          approvalCapabilities.isError ||
          approvalCapabilities.data?.enforcement_enabled !== false
        }
        styleForValue={getInvoiceStatusStyle}
        onChange={(value) => savePatch('Status', { status: value }, () => undefined)}
      />
      <InlineChoiceControl
        value={invoice.priority_status || 'none'}
        options={priorityOptions}
        ariaLabel="Inline invoice priority"
        disabled={updateInvoice.isPending}
        styleForValue={getInvoicePriorityStyle}
        onChange={(value) => savePatch('Priority', { priority_status: value }, () => undefined)}
      />
      <InvoiceAssigneeControl
        currentUser={invoice.assigned_user}
        assignedSource={invoice.assigned_source}
        saving={updateInvoice.isPending}
        onAssign={(user, done) =>
          savePatch('Assignee', { assigned_user_id: user?.id ?? null }, done)
        }
      />
    </>
  )
}

/** Link the invoice to a PO by number, with the amount it bills on that PO. */
export function LinkPoPopover({ summary }: { summary: ReturnType<typeof useInvoiceSummary> }) {
  const { linkPo, poNumber, setPoNumber, poAllocationAmount, setPoAllocationAmount, submitPoLink } =
    summary
  return (
    <ActionPopover
      align="end"
      trigger={
        <Button size="sm" variant="outline">
          <Plus className="h-3.5 w-3.5" /> Link PO
        </Button>
      }
      title="Link a PO"
      confirmLabel="Link PO"
      pending={linkPo.isPending}
      pendingLabel="Linking..."
      onConfirm={(_note, close) => submitPoLink(close)}
    >
      <InlineFieldInput
        value={poNumber}
        onChange={(event) => setPoNumber(event.target.value)}
        placeholder="PO number"
        aria-label="PO number"
        className="h-8 text-sm"
      />
      <InlineFieldInput
        value={poAllocationAmount}
        onChange={(event) => setPoAllocationAmount(event.target.value)}
        inputMode="decimal"
        placeholder="Amount on this PO (optional)"
        aria-label="Amount allocated to purchase order"
        className="h-8 text-sm"
      />
    </ActionPopover>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b py-1.5 last:border-b-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium tabular-nums">{children}</dd>
    </div>
  )
}

/** Dates, terms, balance and payment policy, inside the folded Payment section. */
export function InvoicePaymentDetails({
  invoice,
  summary,
}: {
  invoice: ApInvoiceDetail
  summary: ReturnType<typeof useInvoiceSummary>
}) {
  const early = invoice.early_payment_terms
  const cardFee = useInvoiceCardFee(invoice.uuid).data
  return (
    <div className="space-y-3 text-sm">
      <dl aria-label="Invoice date and amount summary" className="grid gap-x-8 sm:grid-cols-2">
        <Fact label="Pay by">
          {invoice.payment_part
            ? invoice.payment_part.due_date
              ? compactDate(invoice.payment_part.due_date)
              : invoice.payment_part.due_on === 'ship'
                ? 'When it ships'
                : 'On delivery'
            : compactDate(
                invoice.effective_due_date ||
                  invoice.priority_due_date ||
                  invoice.official_due_date,
              )}
        </Fact>
        {invoice.payment_part?.part === 'deposit' && (
          <Fact label="Deposit">{formatMoney(invoice.payment_part.amount)}</Fact>
        )}
        <Fact label="Due">{compactDate(invoice.official_due_date)}</Fact>
        {invoice.priority_due_date && (
          <Fact label="Early due">{compactDate(invoice.priority_due_date)}</Fact>
        )}
        <Fact label="Invoice date">{compactDate(invoice.invoice_date)}</Fact>
        <Fact label="Terms">{formatTerms(invoice)}</Fact>
        {cardFee?.through_vendor && invoice.uuid ? (
          <InvoiceCardFeeLine invoiceUuid={invoice.uuid} fee={cardFee} />
        ) : cardFee ? (
          <Fact label="Card fee">
            <span
              className={cardFee.rule ? undefined : 'font-normal text-muted-foreground'}
              title={
                cardFee.rule
                  ? `${cardFee.source_name}: ${cardFee.description}`
                  : `Not known for ${cardFee.source_name}`
              }
            >
              {cardFee.this_payment ?? cardFee.description}
            </span>
          </Fact>
        ) : null}
        <Fact label="Remaining">{formatMoney(invoice.remaining_balance)}</Fact>
        <div className="flex items-center justify-between gap-4 border-b py-1 last:border-b-0">
          <dt className="text-muted-foreground">Payment policy</dt>
          <dd>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-mr-2 h-7"
              onClick={summary.openPolicyEditor}
            >
              {AP_PAYMENT_POLICIES.find((item) => item.value === invoice.payment_policy)?.label ||
                'Normal terms'}
            </Button>
          </dd>
        </div>
      </dl>
      {early && (
        <p aria-label="Early-pay terms">
          <span className="font-medium">
            Remit {formatMoney(early.amount_to_remit)} by {compactDate(early.deadline)}
          </span>{' '}
          <span className="text-muted-foreground">
            ({early.discount_percent}% off, saves {formatMoney(early.discount_amount)})
          </span>
        </p>
      )}
      {/* Holds, deposits and discounts; "Payment-ready by…" only repeats Pay by. */}
      {invoice.payment_readiness_reason &&
        !invoice.payment_readiness_reason.startsWith('Payment-ready') && (
          <p className="text-xs text-muted-foreground">{invoice.payment_readiness_reason}</p>
        )}
    </div>
  )
}

/** The invoice's own fields, PO allocations and notes, inside the folded Invoice details section. */
export function InvoiceDetailFields({
  invoice,
  invoiceId,
  summary,
  updateInvoice,
  vendorName,
  manufacturer,
}: {
  invoice: ApInvoiceDetail
  invoiceId: string
  summary: ReturnType<typeof useInvoiceSummary>
  updateInvoice: ReturnType<typeof useUpdateInvoice>
  vendorName: string
  manufacturer: ApInvoiceDetail['manufacturer']
}) {
  const { savePatch } = summary
  return (
    <div className="divide-y text-sm">
      <InlineInvoiceField
        label="Invoice number"
        value={invoice.invoice_number || ''}
        displayValue={invoice.invoice_number || 'Missing invoice number'}
        saving={updateInvoice.isPending}
        onConfirm={(value, done) =>
          savePatch('Invoice number', { invoice_number: toNullable(value) }, done)
        }
      />
      <InlineInvoiceField
        label="Vendor name"
        value={invoice.vendor_raw || invoice.vendor?.name || ''}
        displayValue={vendorName}
        saving={updateInvoice.isPending}
        onConfirm={(value, done) => savePatch('Vendor name', { vendor_raw: toNullable(value) }, done)}
      />
      <InlineInvoiceField
        label="Invoice total"
        value={String(invoice.invoice_total ?? '')}
        displayValue={formatMoney(invoice.invoice_total)}
        type="number"
        saving={updateInvoice.isPending}
        onConfirm={(value, done) => {
          const error = validateAmount('Invoice total', value)
          if (error) {
            toast.error(error)
            return
          }
          savePatch('Invoice total', { invoice_total: value || null }, done)
        }}
      />
      <InlineInvoiceField
        label="Ship-to"
        value={invoice.project_ship_to_raw || ''}
        saving={updateInvoice.isPending}
        onConfirm={(value, done) =>
          savePatch('Ship-to', { project_ship_to_raw: toNullable(value) }, done)
        }
      />
      {manufacturer?.uuid && (
        <SummaryReadOnlyRow label="Manufacturer">
          <Link
            to="/manufacturers/$manuId"
            params={{ manuId: manufacturer.uuid }}
            className="text-primary hover:underline"
          >
            {manufacturer.name}
          </Link>
        </SummaryReadOnlyRow>
      )}
      {(invoice.po_links ?? []).length > 0 && (
        <SummaryReadOnlyRow label="Amount per PO">
          <span className="-mx-1.5 block divide-y">
            {(invoice.po_links ?? []).map((link) => (
              <PoAllocationRow key={link.id} invoiceId={invoiceId} link={link} />
            ))}
          </span>
        </SummaryReadOnlyRow>
      )}
      <InlineInvoiceField
        label="Accounting note"
        value={invoice.notes_lesley || ''}
        type="textarea"
        saving={updateInvoice.isPending}
        onConfirm={(value, done) =>
          savePatch('Accounting note', { notes_lesley: toNullable(value) }, done)
        }
      />
      <InlineInvoiceField
        label="PC note"
        value={invoice.notes_pc || ''}
        type="textarea"
        saving={updateInvoice.isPending}
        onConfirm={(value, done) => savePatch('PC note', { notes_pc: toNullable(value) }, done)}
      />
    </div>
  )
}

export function InvoiceSummaryDialogs({
  summary,
  updateInvoice,
}: {
  summary: ReturnType<typeof useInvoiceSummary>
  updateInvoice: ReturnType<typeof useUpdateInvoice>
}) {
  const {
    policyOpen,
    setPolicyOpen,
    policyDraft,
    setPolicyDraft,
    policyReason,
    setPolicyReason,
    recurringInstallmentAmount,
    setRecurringInstallmentAmount,
    recurringTotalCommitment,
    setRecurringTotalCommitment,
    nextExpectedInvoiceDate,
    setNextExpectedInvoiceDate,
    savePaymentPolicy,
  } = summary
  return (
    <>
      <Dialog open={policyOpen} onOpenChange={setPolicyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set payment policy</DialogTitle>
            <DialogDescription>
              This changes payment readiness only. It never initiates or schedules a transfer of
              funds.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Payment policy</Label>
              <Select
                value={policyDraft}
                onValueChange={(value) => setPolicyDraft(value as ApPaymentPolicy)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AP_PAYMENT_POLICIES.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment-policy-reason">Policy reason</Label>
              <ComposeTextarea
                id="payment-policy-reason"
                value={policyReason}
                onChange={(event) => setPolicyReason(event.target.value)}
              />
            </div>
            {policyDraft === 'recurring_installment' && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="recurring-installment-amount">Installment amount</Label>
                  <InlineFieldInput
                    id="recurring-installment-amount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={recurringInstallmentAmount}
                    onChange={(event) => setRecurringInstallmentAmount(event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recurring-total-commitment">Total commitment</Label>
                  <InlineFieldInput
                    id="recurring-total-commitment"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={recurringTotalCommitment}
                    onChange={(event) => setRecurringTotalCommitment(event.target.value)}
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="next-expected-invoice-date">Next expected invoice date</Label>
                  <InlineFieldInput
                    id="next-expected-invoice-date"
                    type="date"
                    value={nextExpectedInvoiceDate}
                    onChange={(event) => setNextExpectedInvoiceDate(event.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPolicyOpen(false)}>
              Cancel
            </Button>
            <LoadingButton
              loading={updateInvoice.isPending}
              loadingText="Saving..."
              onClick={savePaymentPolicy}
            >
              Save policy
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
